// An event's kinds: the primary `host` plus any additional `tags`.
//
// `host` is one value and stays that way - it is what the card colour, the
// chip label, ?hosts= slugs and lyh_filter_groups.hosts all key on, and it is
// what a submitter picks. But an event can genuinely be two things at once: a
// Live Music open mic that is also comedy, a trivia night that is also comedy.
// Filing it under only one means the reader who ticks the OTHER box never sees
// it. `tags` carries the extra kinds, drawn from the SAME vocabulary as host
// (the "Comedy" tag on an open mic is the same string as the "Comedy" host on
// a stand-up show, so one drawer checkbox covers both).
//
// Every surface that asks "is this event a <kind>?" - the drawer's counts and
// filter, a filter group's hosts bundle, search - reads through eventKinds so
// they cannot disagree about what an event is. Surfaces that show ONE thing
// (the card colour) keep reading host directly; that is the primary on purpose.
//
// Tags are moderator-only, like occasions: the submit form offers one host,
// and the approval queue is where a second kind gets added.

/** What an unfiled event is counted under. Mirrors App.tsx's host bucketing. */
export const OTHER_KIND = 'Other'

/** Primary kind first, then tags, deduped. Never empty: a null host is 'Other'. */
export function eventKinds(e: { host: string | null; tags?: string[] | null }): string[] {
  const out: string[] = [e.host ?? OTHER_KIND]
  for (const t of e.tags ?? []) {
    if (t && !out.includes(t)) out.push(t)
  }
  return out
}

/** The tags an event carries beyond its host - what a second chip should show. */
export function extraKinds(e: { host: string | null; tags?: string[] | null }): string[] {
  return eventKinds(e).slice(1)
}

/** True when the event is any of the selected kinds (host OR a tag). */
export function hasAnyKind(
  e: { host: string | null; tags?: string[] | null },
  selected: ReadonlySet<string> | readonly string[],
): boolean {
  const has = selected instanceof Set ? (k: string) => selected.has(k) : (k: string) => (selected as readonly string[]).includes(k)
  return eventKinds(e).some(has)
}
